import { useEffect, useState } from "react";
import { api, ApiError } from "./api";
import { setTelegramBackHandler } from "./telegram";
import type { NotificationEvent } from "./types";

function eventTime(value: string): string {
  const date = new Date(value.replace(" ", "T") + (/Z|[+-]\d\d:\d\d$/.test(value) ? "" : "Z"));
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat("ru-RU", { timeZone: "Europe/Moscow", dateStyle: "short", timeStyle: "short" }).format(date) + " МСК";
}

export function NotificationCenter({ eventId, demo, onBack, onOpenEvent, onOpenGame }: {
  eventId: number | null; demo: boolean; onBack: () => void;
  onOpenEvent: (id: number) => void; onOpenGame: (id: number) => void;
}) {
  const [events, setEvents] = useState<NotificationEvent[]>([]);
  const [detail, setDetail] = useState<NotificationEvent | null>(null);
  const [cursor, setCursor] = useState<number | null>(null);
  const [pending, setPending] = useState(!demo);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const resolve = async (gameId: number, action: "remove" | "keep") => {
    if (!detail || pending || (action === "remove" && !window.confirm("Удалить игру из отслеживания?"))) return;
    setPending(true); setError(null);
    try {
      const result = await api.resolveSteamNotification(detail.id, gameId, action);
      setDetail(await api.notificationEvent(detail.id));
      setActionMessage(result.changed ? (action === "keep" ? "Игра оставлена в отслеживании." : "Игра удалена из отслеживания.") : "Решение уже принято или игра вернулась в Steam Wishlist.");
    } catch { setError("Не удалось выполнить действие. Обновите уведомление и попробуйте снова."); }
    finally { setPending(false); }
  };
  useEffect(() => setTelegramBackHandler(onBack), [onBack]);
  useEffect(() => {
    let active = true;
    setDetail(null); setError(null);
    if (demo) { setPending(false); return; }
    setPending(true);
    const load = async () => {
      if (eventId !== null) {
        const event = await api.notificationEvent(eventId);
        if (active) setDetail(event);
      } else {
        const response = await api.notificationEvents();
        if (active) { setEvents(response.events); setCursor(response.next_cursor); }
      }
    };
    void load().catch(reason => { if (active) setError(reason instanceof ApiError && reason.status === 404 ? "Уведомление недоступно." : "Не удалось загрузить уведомления."); }).finally(() => { if (active) setPending(false); });
    return () => { active = false; };
  }, [eventId, demo]);
  const more = async () => {
    if (cursor === null || pending) return;
    setPending(true); setError(null);
    try { const response = await api.notificationEvents(cursor); setEvents(current => [...current, ...response.events]); setCursor(response.next_cursor); }
    catch { setError("Не удалось загрузить остальные уведомления."); }
    finally { setPending(false); }
  };
  const target = detail?.target_url?.match(/^\/#\/game\/(\d+)$/);
  return <main className="catalog-page notification-center">
    <button className="browser-back" type="button" onClick={onBack}>← Назад</button>
    <h1>{detail?.title ?? "Уведомления"}</h1>
    {error ? <p role="alert">{error}</p> : null}
    {actionMessage ? <p role="status">{actionMessage}</p> : null}
    {pending ? <p role="status">Загружаем…</p> : null}
    {eventId !== null && detail ? <section className="notification-detail">
      <time>{eventTime(detail.created_at)}</time>
      {Array.from(new Set(detail.actions?.map(action => action.game_id))).map(gameId => <article key={gameId} className="notification-entry">
        <strong>{detail.actions?.find(action => action.game_id === gameId)?.title ?? `Игра #${gameId}`}</strong>
        <div className="button-row">{detail.actions?.filter(action => action.game_id === gameId).map(action => <button key={action.action} type="button" disabled={pending} onClick={() => void resolve(action.game_id, action.action)}>{action.action === "remove" ? "Удалить из отслеживания" : "Оставить в отслеживании"}</button>)}</div>
      </article>)}
      {detail.entries.length ? detail.entries.map(entry => <article key={entry.game_id} className="notification-entry"><p>{entry.text}</p><button type="button" onClick={() => onOpenGame(entry.game_id)}>Открыть {entry.title}</button></article>) : <><p>{detail.body}</p>{detail.targets?.length ? detail.targets.map(item => <button key={item.game_id} type="button" onClick={() => onOpenGame(item.game_id)}>Открыть {item.title}</button>) : target ? <button type="button" onClick={() => onOpenGame(Number(target[1]))}>Открыть карточку игры</button> : null}</>}
    </section> : eventId === null ? <section className="notification-list" aria-label="История уведомлений">
      {!pending && !events.length && !error ? <p>Пока нет уведомлений.</p> : null}
      {events.map(event => <button key={event.id} type="button" className="notification-entry" onClick={() => onOpenEvent(event.id)}><strong>{event.title}</strong><time>{eventTime(event.created_at)}</time><span>{event.body.slice(0, 180)}{event.body.length > 180 ? "…" : ""}</span></button>)}
      {cursor !== null ? <button type="button" disabled={pending} onClick={() => void more()}>Показать ещё</button> : null}
    </section> : null}
  </main>;
}
